import React from "react";
import { ArrowRight, Activity, Brain, Building2, CheckCircle, Clock, Cog, RefreshCw, Target } from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";
import EosBackdrop from "../EosBackdrop";
import { useDashboard } from "./useDashboard";

const Dashboard: React.FC = () => {
  const {
    user,
    topStocks,
    loadingStocks,
    stockError,
    appliedModel,
    loadingModel,
    realBalance,
    loadingBalance,
    handleBrokerSettings,
    handleViewModels,
    handleViewTrades,
    fetchRealBalance,
    fetchTopStocks,
    isMarketOpen,
  } = useDashboard();

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden text-slate-100">
      <EosBackdrop />
      <Header />
      <main className="relative z-10 max-w-7xl mx-auto w-full flex-1 py-4 sm:py-6 px-3 sm:px-4 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div
            className={`glass-card p-4 ${user?.broker === "none" ? "cursor-pointer hover:shadow-lg transition-all duration-200" : ""}`}
            onClick={user?.broker === "none" ? handleBrokerSettings : undefined}
          >
            <div className="flex items-center">
              <div className="bg-emerald-500/10 p-2 rounded-lg mr-3">
                <Building2 className="h-5 w-5 text-emerald-300" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Broker</p>
                {user?.broker === "none" ? (
                  <div className="flex items-center">
                    <span className="text-sm font-semibold text-emerald-300">Connect Broker</span>
                    <ArrowRight className="h-3 w-3 ml-1 text-emerald-300" />
                  </div>
                ) : user?.broker === "connecting" ? (
                  <p className="text-sm font-semibold text-orange-300">Connecting...</p>
                ) : (
                  <p className="text-sm font-semibold text-slate-100">{String(user?.broker)}</p>
                )}
              </div>
            </div>
          </div>

          <div
            className={`glass-card p-4 ${user?.broker === "none" ? "cursor-pointer hover:shadow-lg transition-all duration-200" : ""}`}
            onClick={user?.broker === "none" ? handleBrokerSettings : undefined}
          >
            <div className="flex items-center">
              <div className="bg-green-500/10 p-2 rounded-lg mr-3">
                <Target className="h-5 w-5 text-green-300" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Account Balance</p>
                {loadingBalance ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-400" />
                ) : user?.broker === "zerodha" && realBalance !== null ? (
                  <div className="flex items-center">
                    <p className="text-sm font-semibold text-slate-100">₹{realBalance.toLocaleString("en-IN")}</p>
                    <button
                      onClick={() => void fetchRealBalance()}
                      className="ml-2 text-green-400 hover:text-green-300 transition-colors"
                    >
                      <RefreshCw className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <p className="text-sm font-semibold text-slate-100">Not Connected</p>
                )}
              </div>
            </div>
          </div>

          <div className="glass-card p-4">
            <div className="flex items-center">
              <div className="bg-purple-500/10 p-2 rounded-lg mr-3">
                <Brain className="h-5 w-5 text-purple-300" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Applied Model</p>
                {loadingModel ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-400" />
                ) : appliedModel ? (
                  <p className="text-sm font-semibold text-slate-100">
                    {String(appliedModel.name || appliedModel?.id || "Active Model")}
                  </p>
                ) : (
                  <p className="text-sm font-semibold text-slate-100">No Model Applied</p>
                )}
              </div>
            </div>
          </div>

          <div className="glass-card p-4">
            <div className="flex items-center">
              <div className="bg-accent-500/10 p-2 rounded-lg mr-3">
                <Activity className="h-5 w-5 text-accent-300" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Market Status</p>
                <p className="text-sm font-semibold text-slate-100">{isMarketOpen() ? "Open" : "Closed"}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
          <div className="lg:col-span-2 glass-card p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-100">Top Stocks</h2>
                <p className="text-sm text-slate-400">AI-generated insights for today</p>
              </div>
              <button
                onClick={() => void fetchTopStocks()}
                className="flex items-center text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>

            {loadingStocks ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400" />
              </div>
            ) : stockError ? (
              <div className="text-center py-8 text-slate-400">{stockError}</div>
            ) : topStocks.length > 0 ? (
              <div className="space-y-3">
                {topStocks.map((stock, index) => (
                  <div
                    key={`${stock.symbol || "stock"}-${index}`}
                    className="flex items-center justify-between p-3 rounded-lg bg-white/5"
                  >
                    <div>
                      <p className="font-semibold text-slate-100">{stock.symbol || "Unknown"}</p>
                      <p className="text-sm text-slate-400">{stock.name || "No name available"}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-emerald-300">{stock.action || "Watch"}</p>
                      <p className="text-sm text-slate-500">Confidence: {stock.confidence ?? 0}%</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">No recommendations available.</div>
            )}
          </div>

          <div className="glass-card p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-100">Trading Overview</h2>
                <p className="text-sm text-slate-400">Current portfolio health</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-emerald-500/10">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-emerald-300 mr-2" />
                  <div>
                    <p className="font-semibold text-slate-100">Strategy Active</p>
                    <p className="text-sm text-slate-400">Your automated workflow is ready.</p>
                  </div>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-accent-500/10">
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-accent-300 mr-2" />
                  <div>
                    <p className="font-semibold text-slate-100">Market Timing</p>
                    <p className="text-sm text-slate-400">
                      {isMarketOpen() ? "Market is currently open." : "Market is closed."}
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-purple-500/10">
                <div className="flex items-center">
                  <Cog className="h-5 w-5 text-purple-300 mr-2" />
                  <div>
                    <p className="font-semibold text-slate-100">Manage Models</p>
                    <p className="text-sm text-slate-400">Adjust strategy settings from the models screen.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="glass-card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-slate-100">Quick Actions</h2>
              <p className="text-sm text-slate-400">Move between key sections</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleBrokerSettings}
              className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
            >
              Broker Settings
            </button>
            <button
              onClick={handleViewModels}
              className="px-4 py-2 rounded-lg border border-white/15 text-emerald-300 hover:bg-white/5 transition-colors"
            >
              View Models
            </button>
            <button
              onClick={handleViewTrades}
              className="px-4 py-2 rounded-lg border border-white/15 text-slate-300 hover:bg-white/5 transition-colors"
            >
              View Trades
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Dashboard;
